import { useCurrentOrganizationContext } from "@/contexts";
import {
  useActiveScopeCapabilities,
  useAuditLogPermissions,
  useCanAccessPlatformSettings,
  useCanSeeOrganizationData,
  useCanViewPlatformSettings,
  useClubPermissions,
  useCoachPermissions,
  useIsLicensesEnabled,
  useLeaguePermissions,
  useLicensePermissions,
  useOrganizationCapabilities,
  useOrganizationPermissions,
  usePartnerPermissions,
  usePlatformCapabilities,
  useTeamPermissions,
  useUserPermissions,
} from "@/hooks";
import { hasCapability, hasFinanceRead, hasReportRead } from "@/utils/capability";

import { NavLinkItemType } from "./NavLink.types";

const ORGANIZATION_DATA_NAV_ITEMS = new Set<NavLinkItemType>([
  "managementTeam",
  "organizations",
  "licenses",
  "issuedLicenses",
  "certificates",
  "issuedCertificates",
  "applicationForms",
  "issuedApplicationForms",
  "clubs",
  "venues",
  "users",
  "coaches",
  "teams",
  "transactions",
  "platformStripeAccounts",
  "pages",
  "news",
  "gallery",
  "documents",
  "banners",
  "partners",
  "integrations",
]);

// Org-specific surfaces that make no sense without a current organization, so
// they are hidden in platform-only mode (the data lists in
// ORGANIZATION_DATA_NAV_ITEMS stay visible — they show platform-wide data).
const PLATFORM_ONLY_HIDDEN_NAV_ITEMS = new Set<NavLinkItemType>(["organizationSettings"]);

export const useNavLinkItemFilter = () => {
  const { organization, isPlatformOnly } = useCurrentOrganizationContext();
  const canViewPlatformSettings = useCanViewPlatformSettings();
  const canAccessPlatformSettings = useCanAccessPlatformSettings();
  const canSeeOrganizationData = useCanSeeOrganizationData();
  const { canViewOrganization, canUpdateOrganization } = useOrganizationPermissions();
  const { canSeeUsers } = useUserPermissions();
  const { canSeeLicenses } = useLicensePermissions();
  const { canSeeTeams } = useTeamPermissions();
  const { canSeeClubs } = useClubPermissions();
  const { canSeeCoaches } = useCoachPermissions();
  const { canSeePartners } = usePartnerPermissions();
  const { canSeeLeagues } = useLeaguePermissions();
  const { canSeeAuditLogs } = useAuditLogPermissions();
  const platformCapabilities = usePlatformCapabilities();
  const organizationCapabilities = useOrganizationCapabilities();
  const { level, capabilities: activeScopeCapabilities } = useActiveScopeCapabilities();

  const canSeePlatformManagers = hasCapability(platformCapabilities, "PLATFORM_MANAGER:READ");

  const isLicensesEnabled = useIsLicensesEnabled();

  // Capability grant for the org-data items that own a READ capability key (see
  // docs/capabilities.md). Items missing here (organizations, venues, news,
  // gallery, integrations) have no read key in the FE vocabulary and fall back
  // to the scope-membership predicate. News stays on the member predicate on
  // purpose — its list is read-open (no list-level gate).
  const capabilityGrants: Partial<Record<NavLinkItemType, boolean>> = {
    // Management team rides the ORG_MANAGER keys under an organization and the
    // platform-level PLATFORM_MANAGER analog in platform-only mode (the page
    // then shows the platform-wide team) — mirroring
    // useManagementTeamPermissions (the page's guard).
    managementTeam:
      organization !== null
        ? hasCapability(organizationCapabilities, "ORG_MANAGER:READ")
        : hasCapability(platformCapabilities, "PLATFORM_MANAGER:READ"),
    licenses: canSeeLicenses,
    issuedLicenses: canSeeLicenses,
    certificates: canSeeLicenses,
    issuedCertificates: canSeeLicenses,
    applicationForms: canSeeLicenses,
    issuedApplicationForms: canSeeLicenses,
    clubs: canSeeClubs,
    users: canSeeUsers,
    coaches: canSeeCoaches,
    teams: canSeeTeams,
    // Partners list gates on the active scope's `<LEVEL>_PARTNER:READ`.
    partners: canSeePartners,
    // Banners are platform-only appearance content — `PLATFORM_BANNER:READ`.
    banners: hasCapability(platformCapabilities, "PLATFORM_BANNER:READ"),
    // Own key or an ancestor's (a platform finance manager's
    // PLATFORM_FINANCE:READ appears in the org set via the ancestor walk). In
    // platform-only mode the page lists platform-wide transactions, so it rides
    // the active scope's set — mirroring useTransactionPermissions (the page's
    // guard).
    transactions:
      organization !== null
        ? hasFinanceRead(organizationCapabilities, "organization")
        : hasFinanceRead(activeScopeCapabilities, level),
    platformStripeAccounts: hasCapability(platformCapabilities, "PLATFORM_FINANCE:READ"),
    // Documents have no key of their own — they ride `ORG:WRITE` on the org set
    // in an organization context and `PLATFORM_SETTINGS:WRITE` in platform-only
    // mode, mirroring `usePlatformDocumentPermissions` (the page's guard).
    documents:
      organization !== null
        ? hasCapability(organizationCapabilities, "ORG:WRITE")
        : canAccessPlatformSettings,
    // Home page builder (Pages) has no key of its own — it rides
    // `PLATFORM_SETTINGS:WRITE`, mirroring the Pages page guard.
    pages: canAccessPlatformSettings,
  };

  const filterNavLinkItem = (type: NavLinkItemType): boolean => {
    if (isPlatformOnly && PLATFORM_ONLY_HIDDEN_NAV_ITEMS.has(type)) return false;
    if (type === "platformManagers") return canSeePlatformManagers;
    // The update-platform page is reachable with any platform capability (it
    // renders read-only without `PLATFORM_SETTINGS:WRITE`).
    if (type === "platformSettings") return canViewPlatformSettings;
    // Own audit key (or an ancestor's) on the active level's capability set.
    if (type === "auditLogs")
      return canSeeAuditLogs(organization !== null ? "organization" : "platform");
    // The update-organization page is reachable with `ORG:READ` alone (it
    // renders read-only without `ORG:WRITE`).
    if (type === "organizationSettings") return canViewOrganization || canUpdateOrganization;
    // Home (dashboard analytics) mirrors DashboardPage's report-read gate.
    if (type === "home") return hasReportRead(activeScopeCapabilities, level);
    // "Tours & Leagues" surface (leagues list + tournaments) is gated by the
    // platform-level module flag, surfaced as the shared `canSeeLeagues`
    // predicate (the LeaguesPage ForbiddenPage gate consumes the same one). The
    // read surfaces themselves stay capability-ungated (visible to everyone in
    // scope).
    if ((type === "leagues" || type === "tournaments") && !canSeeLeagues) return false;
    if (type === "licenses" && !isLicensesEnabled) return false;
    if (type === "issuedLicenses" && !isLicensesEnabled) return false;
    if (type === "certificates" && !organization?.enableCertificates) return false;
    if (type === "issuedCertificates" && !organization?.enableCertificates) return false;
    if (type === "applicationForms" && !organization?.enableApplicationForms) return false;
    if (type === "issuedApplicationForms" && !organization?.enableApplicationForms) return false;
    // Interim, pre-RBAC gate: hide Transactions when the current organization is in the
    // caller's transaction-exclusion set (a self-owning member federation reached only via a
    // parent platform they manage). `canViewTransactions` comes from the available-federation
    // REST projection. Platform-only mode has no current organization to exclude (the BE
    // list scope drops excluded federations per row), so the item stays visible there —
    // mirroring the TransactionsPage guard. Remove once RBAC permission checks replace it.
    if (type === "transactions" && organization !== null && !organization.canViewTransactions)
      return false;
    // Org-data items: gate on the item's own READ key when it has one; the
    // keyless surfaces (news/gallery reads, venues, documents, …) fall back to
    // scope membership so they stay visible to owners/managers after the strict
    // cutover. See docs/capabilities.md.
    if (ORGANIZATION_DATA_NAV_ITEMS.has(type))
      return capabilityGrants[type] ?? canSeeOrganizationData;
    return true;
  };

  return filterNavLinkItem;
};
